import { computed, inject } from '@angular/core';
import { AuthResDto, CredentialsDto, UserResDto } from '@api/models';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { of, pipe, switchMap } from 'rxjs';
import { AuthControllerSetSessionOrganizzazione$Params } from '../../../api/functions';
import { AuthService, JwtService } from '../services';

export type AuthState = {
  currentUser: UserResDto | null;
  token: string | null;
  error: unknown | null;
  organizzazioneSelected: AuthResDto['organizzazioni'][0] | null;
  organizzazioni: AuthResDto['organizzazioni'];
};

const initialState: AuthState = {
  currentUser: null,
  token: null,
  error: null,
  organizzazioneSelected: null,
  organizzazioni: [],
};

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState<AuthState>(initialState),
  withComputed((store) => ({
    fullName: computed(() => {
      const user = store.currentUser();
      return user ? `${user.firstName} ${user.lastName}` : '';
    }),
    initials: computed(() => {
      const user = store.currentUser();
      if (!user) return '';
      return (user.firstName.charAt(0) + user.lastName.charAt(0)).toUpperCase();
    }),
    isAuthenticated: computed(() => !!store.token() && !!store.currentUser()),
    pathLogo: computed(() => {
      const user = store.currentUser();
      return user?.organizzazione?.cartellaImmagini
        ? `assets/images/${user.organizzazione.cartellaImmagini}/logo.png`
        : 'assets/images/logo.png';
    }),
  })),
  withMethods((store, jwtService = inject(JwtService)) => ({
    saveToken(token: string) {
      jwtService.saveToken(token);
      patchState(store, { token });
    },
    destroyToken() {
      jwtService.destroyToken();
      patchState(store, { token: null });
    },
    setAuth(user: UserResDto) {
      patchState(store, {
        token: user.token,
        currentUser: user,
        error: null,
        organizzazioneSelected: user.organizzazione,
        organizzazioni: user.accountOrganizzazioni,
      });
      jwtService.saveToken(user.token);
    },
    purgeAuth() {
      jwtService.destroyToken();

      patchState(store, {
        token: null,
        currentUser: null,
        error: null,
        organizzazioneSelected: null,
        organizzazioni: [],
      });
    },
  })),
  withMethods((store, authService = inject(AuthService)) => ({
    populate: rxMethod<void>(
      pipe(
        switchMap(() => {
          const token = store.token();
          const path = window.location.pathname;

          const excludedPaths = [
            '/login',
            '/recupero-password',
            '/primo-accesso',
          ];
          if (!token || excludedPaths.includes(path)) {
            store.purgeAuth();
            patchState(store, { error: 'No token found' });
            return of(null);
          }
          return authService.userMe().pipe(
            tapResponse({
              next: (user) => {
                store.setAuth(user);
              },
              error: (err) => {
                store.purgeAuth();
                patchState(store, { error: err });
              },
            }),
          );
        }),
      ),
    ),
    attemptAuth: rxMethod<CredentialsDto>(
      pipe(
        switchMap((credentials) =>
          authService.attemptAuth(credentials).pipe(
            tapResponse({
              next: (user) => {
                store.saveToken(user.token);
              },
              error: (err) => {
                store.purgeAuth();
                patchState(store, { error: err });
              },
            }),
          ),
        ),
        switchMap((res) => {
          const organizzazioni = res?.organizzazioni || [];
          const organizzazioneSelected =
            organizzazioni.length === 1 ? organizzazioni[0] : null;
          patchState(store, {
            organizzazioni,
            organizzazioneSelected,
          });
          if (!organizzazioneSelected) {
            return of(null);
          }
          return authService.userMe().pipe(
            tapResponse({
              next: (user) => store.setAuth(user),
              error: (err) => {
                store.purgeAuth();
                patchState(store, { error: err });
              },
            }),
          );
        }),
      ),
    ),
    setSession: rxMethod<AuthControllerSetSessionOrganizzazione$Params>(
      pipe(
        switchMap((params) =>
          authService.setSession(params).pipe(
            tapResponse({
              next: (user) => {
                // store.purgeAuth();
                patchState(store, { currentUser: null, error: null });
                store.saveToken(user.token);
              },
              error: (err) => {
                store.purgeAuth();
                patchState(store, { error: err });
              },
            }),
          ),
        ),
        switchMap((res) => {
          return authService.userMe().pipe(
            tapResponse({
              next: (user) => store.setAuth(user),
              error: (err) => {
                store.purgeAuth();
                patchState(store, { error: err });
              },
            }),
          );
        }),
      ),
    ),
  })),
  withHooks({
    onInit: (store, jwtService = inject(JwtService)) => {
      const token = jwtService.getToken();
      if (token) {
        store.saveToken(token);
      }
    },
  }),
);
